/**
 * VerificationCoordinator - Coordinates toast → modal handoff for verification
 * 
 * This component bridges the gap between:
 * - verificationRequestManager (handles incoming requests, shows toasts)
 * - VerificationModal (handles the actual verification UI)
 * 
 * It must be rendered within ToastProvider context to work properly.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { CryptoEvent } from '../../constants/matrixEvents';
import { useToast } from '../../context/ToastContext';
import verificationRequestManager from '../../services/verificationRequestManager';
import matrixService from '../../services/matrixService';
import VerificationModal from './VerificationModal';
import { trustEventEmitter, TrustEvents } from '../../crypto/events';
import { useI18n } from '../../context/I18nContext';
import { resolveDeviceName } from '../../utils/resolveDeviceName';
import logger from '../../utils/logger';

const VerificationCoordinator = () => {
  const { t } = useI18n();

  const [modalState, setModalState] = useState({
    isOpen: false,
    request: null,
    requestId: null,
    displayName: null,
  });

  // Track if verification recently completed so we can handle gossiped keys
  const verificationCompletedRef = useRef(false);
  const backupRestoredRef = useRef(false);

  const toastContext = useToast();

  /**
   * Handle user clicking "Verify" in toast
   * Opens the verification modal AND accepts the request
   */
  const handleVerifyRequest = useCallback(async (request, requestId) => {
    logger.info('User clicked Verify button, opening verification modal');

    // Get device name for display (shared resolver keeps toast + modal labels in sync)
    const deviceName = await resolveDeviceName(request, { client: matrixService.client, t });

    // IMPORTANT: Accept the request only - don't start verification
    // The INITIATOR will start verification, responder just accepts
    // This matches Element Web's flow for responding to verification requests
    try {
      await request.accept();
    } catch (error) {
      logger.error('VerificationCoordinator: Failed to accept request:', error);
      // Still open modal - it will show the error
    }

    setModalState({
      isOpen: true,
      request,
      requestId,
      displayName: deviceName,
    });

    // Dismiss the toast since we're opening the modal
    toastContext.dismissToast(requestId);
  }, [toastContext, t]);

  // Initialize verification request manager with contexts
  useEffect(() => {
    logger.debug('VerificationCoordinator: Initializing');

    // Initialize the verification request manager with toast context
    verificationRequestManager.initialize(
      toastContext,
      null, // modalContext not needed with callback approach
      matrixService,
      t,
    );

    // Set callback for when user clicks "Verify" in toast
    verificationRequestManager.setOnVerifyRequest(handleVerifyRequest);

    logger.info('VerificationCoordinator: Initialized and ready');

    return () => {
      // Cleanup on unmount
      verificationRequestManager.setOnVerifyRequest(null);
      // Drop the React-bound translator so the singleton doesn't retain a closure
      // over a torn-down tree (e.g. after logout/HMR). Restores the identity fallback.
      verificationRequestManager.setTranslator(null);
    };
  }, [toastContext, handleVerifyRequest, t]);

  // Listen for KeyBackupDecryptionKeyCached to handle race condition
  // where backup key arrives via gossip AFTER verification completes
  useEffect(() => {
    const client = matrixService.client;
    if (!client) return;

    const handleBackupKeyCached = async (backupVersion) => {
      // Only act if verification recently completed and backup wasn't already restored
      if (!verificationCompletedRef.current || backupRestoredRef.current) {
        return;
      }

      logger.info(`VerificationCoordinator: KeyBackupDecryptionKeyCached event received for version ${backupVersion}`);

      try {
        const crypto = client.getCrypto?.();
        if (!crypto) return;

        // Now that the key is cached, we can restore the backup
        if (typeof crypto.restoreKeyBackup === 'function') {
          logger.debug('VerificationCoordinator: Restoring keys from backup after key was cached...');
          try {
            await crypto.restoreKeyBackup();
            logger.info('VerificationCoordinator: Key backup restored successfully after gossip');
            backupRestoredRef.current = true;

            // Emit VERIFICATION_COMPLETE to trigger re-decryption in active rooms
            trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_COMPLETE, {
              verificationType: 'device',
              source: 'verification-coordinator-gossip',
            });
          } catch (backupErr) {
            logger.debug('VerificationCoordinator: Key backup restore failed after gossip:', backupErr.message);
          }
        }
      } catch (err) {
        logger.error('VerificationCoordinator: Error restoring keys after gossip:', err);
      }
    };

    client.on(CryptoEvent.KeyBackupDecryptionKeyCached, handleBackupKeyCached);

    return () => {
      client.off(CryptoEvent.KeyBackupDecryptionKeyCached, handleBackupKeyCached);
    };
  }, []);

  /**
   * Handle modal close
   */
  const handleModalClose = () => {
    logger.debug('VerificationCoordinator: Modal closed');
    
    const { requestId } = modalState;
    
    setModalState({
      isOpen: false,
      request: null,
      requestId: null,
      displayName: null,
    });

    // Clean up the request from active requests
    if (requestId && verificationRequestManager.activeRequests) {
      verificationRequestManager.activeRequests.delete(requestId);
    }
  };

  /**
   * Handle verification complete
   * After successful SAS verification, restore keys from backup to enable message decryption
   */
  const handleVerificationComplete = async (success) => {
    logger.info('VerificationCoordinator: Verification completed:', success);

    const { requestId } = modalState;

    // Clean up
    if (requestId && verificationRequestManager.activeRequests) {
      verificationRequestManager.activeRequests.delete(requestId);
    }

    // If verification succeeded, restore keys from backup
    // This enables decryption of historical messages without page reload
    // Reference: Element Web's SetupEncryptionStore.ts usePassPhrase() approach
    if (success) {
      // Mark that verification completed - used by KeyBackupDecryptionKeyCached handler
      verificationCompletedRef.current = true;

      try {
        const client = matrixService.client;
        if (client) {
          const crypto = client.getCrypto?.();
          if (crypto) {
            // Step 1: Load the backup private key from secret storage (4S)
            // This makes the backup decryption key available to the crypto module
            if (typeof crypto.loadSessionBackupPrivateKeyFromSecretStorage === 'function') {
              logger.debug('VerificationCoordinator: Loading backup key from secret storage...');
              try {
                await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
                logger.info('VerificationCoordinator: Backup key loaded from secret storage');
              } catch (loadErr) {
                // May fail if 4S isn't accessible or key was gossiped differently
                // The KeyBackupDecryptionKeyCached event will fire when key arrives via gossip
                logger.debug('VerificationCoordinator: Failed to load backup key from 4S (will retry when key arrives):', loadErr.message);
              }
            }

            // Step 2: Restore the key backup (download all megolm keys)
            if (typeof crypto.restoreKeyBackup === 'function') {
              logger.debug('VerificationCoordinator: Restoring keys from backup...');
              try {
                await crypto.restoreKeyBackup();
                logger.info('VerificationCoordinator: Key backup restored successfully');
                backupRestoredRef.current = true;
              } catch (backupErr) {
                // Key backup restore may fail if not set up or keys not available
                // The KeyBackupDecryptionKeyCached event will fire when key arrives via gossip
                logger.debug('VerificationCoordinator: Key backup restore failed (will retry when key arrives):', backupErr.message);
              }
            }
          }
        }
      } catch (err) {
        logger.error('VerificationCoordinator: Error restoring keys after verification:', err);
      }
    }

    // Close modal after a brief delay to show success state
    setTimeout(() => {
      handleModalClose();
    }, 1500);
  };

  // Render the verification modal
  // CRITICAL: Use requestId as key to force new component instance per request
  // This ensures complete cleanup of old verification state and listeners
  return (
    <VerificationModal
      key={modalState.requestId || 'closed'}
      isOpen={modalState.isOpen}
      incomingRequest={modalState.request}
      verificationType="device"
      displayName={modalState.displayName}
      onClose={handleModalClose}
      onComplete={handleVerificationComplete}
    />
  );
};

export default VerificationCoordinator;
